import axios from '/node_modules/axios/dist/browser/axios.cjs';

/*
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    ENGINEERING NOTES
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    Import /ezlibrary/helpers/* individualy. Do not import from /ezlibrary/helpers/EzHelpers.js'
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
*/

import { EzBadParamException } from '/ezlibrary/exceptions/EzExceptions.js';
import { EzObject } from '/ezlibrary/helpers/EzObject.js';
import { EzBoolean } from '/ezlibrary/helpers/EzBoolean.js';
import { EzString } from '/ezlibrary/helpers/EzString.js';
import { EzArray } from '/ezlibrary/helpers/EzArray.js';
import { EzJson } from '/ezlibrary/helpers/EzJson.js';
import { EzConsole } from '/ezlibrary/helpers/EzConsole.js';
import { EzPromise } from '/ezlibrary/helpers/EzPromise.js';
import { EzStaticClass } from '/ezlibrary/EzStaticClass.js';

/**
 * @class
 * @extends {EzClass}
 * @description
 * Determines if the current page is using the latest version param. If not, the page is re-loaded with the
 * correct version param.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import { EzVersionChecker } from '/ezlibrary/EzVersionChecker.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Static access only: EzVersionCheck.{property or method}
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzVersionChecker extends EzStaticClass {
    /**
     * @static
     * @public @readonly @property
     * Gets the version url param name: 'v'
     *
     * @type {string}
     */
    static get VERSION_URL_PARAM_NAME() {
        return 'v';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default revision number
     *
     * @returns {string}
     */
    static get DEFAULT_REVISION() {
        return 'r75-5';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default base version constant
     *
     * @returns {string}
     */
    static get DEFAULT_BASE_VERSION() {
        return EzVersionChecker.DEFAULT_REVISION.substring(1);
    }

    /**
     * @static
     * @public @readonly @property
     * Default website version for this branch/build
     *
     * @returns {string}
     */
    static get DEFAULT_WEBSITE_VERSION() {
        return `r${EzVersionChecker.DEFAULT_BASE_VERSION}`;
    }

    /**
     * @public @readonly @property
     * Default service version for this branch/build
     *
     * @returns {string}
     */
    static get DEFAULT_SERVICE_VERSION() {
        return `v${EzVersionChecker.DEFAULT_BASE_VERSION}.0.x`;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default ezClocker version
     * Template: `${Date.now().year}.01.01-${EzVersionChecker.DEFAULT_BASE_VERSION}.0.x`
     * Example: '2024.01.01-50.0.x'
     *
     * @returns {string}
     */
    static get DEFAULT_BUILD_VERSION() {
        return `${Date.now().year}.01.01-${EzVersionChecker.DEFAULT_BASE_VERSION}.0.x`;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default ezClocker version
     * Template: `releases/r${EzVersionChecker.DEFAULT_BASE_VERSION}.0.x`;
     * Example: 'releases/r50.0.x'
     *
     * @returns {string}
     */
    static get DEFAULT_BUILD_BRANCH() {
        return `releases/r${EzVersionChecker.DEFAULT_BASE_VERSION}.0.x`;
    }

    /**
     * @static
     * @private @field
     * Stores the default build date
     *
     * @type {string}
     */
    static #DEFAULT_BUILD_DATE = EzString.EMPTY;
    /**
     * @static
     * @public @readonly @property
     * Gets the default ezClocker version
     * Template: `${Date.now().year}-01-01T00:00:00Z`;
     * Example: '2024-01-01T00:00:00Z'
     *
     * @returns {string}
     */
    static get DEFAULT_BUILD_DATE() {
        if (EzString.hasLength(this.#DEFAULT_BUILD_DATE)) {
            this.#DEFAULT_BUILD_DATE = new Date().toISOString();
        }

        return this.#DEFAULT_BUILD_DATE;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default environment id
     *
     * @returns {string}
     */
    static get DEFAULT_BUILD_ENVIRONMENT() {
        return 'loc';
    }

    /**
     * @private @field
     * Stores the ezclocker.website.version value from version.properties file
     * @type {string}
     */
    static #ezWebsiteVersion = EzString.EMPTY;
    /**
     * @public @property @getter
     * Gets the ezclocker.website.version value from version.properties file
     *
     * @returns {string}
     */
    static get ezWebsiteVersion() {
        if (!EzString.hasLength(this.#ezWebsiteVersion)) {
            this.#ezWebsiteVersion = EzVersionChecker.DEFAULT_WEBSITE_VERSION;
        }

        return this.#ezWebsiteVersion;
    }
    /**
     * @public @property @setter
     * Sets the ezclocker.website.version value from version.properties file
     * @param {string} websiteVersion
     */
    static set ezWebsiteVersion(websiteVersion) {
        this.#ezWebsiteVersion = EzString.stringOrDefault(websiteVersion, EzVersionChecker.DEFAULT_WEBSITE_VERSION);
    }
    /**
     * @public @readonly @property
     * Gets the ezclocker.website.version value from version.properties file
     *
     * @returns {string}
     *
     * @deprecated
     * Migrate to: EzVersionChcker.ezWebsiteVersion
     */
    static get websiteVersion() {
        return this.ezWebsiteVersion;
    }

    /**
     * @public @readonly @property
     * Gets the website revision parameter: v={this.ezWebsiteVersion}
     * @returns {string}
     */
    static get ezWebsiteRevisionVersion() {
        return EzString.build`v=${this.ezWebsiteVersion}`;
    }

    /**
     * @private @field
     * Stores the ezclocker.services.version value from version.properties file
     *
     * @type {string}
     */
    static #ezServicesVersion = EzString.EMPTY;
    /**
     * @public @property @getter
     * Gets the ezclocker.services.version value from version.properties file
     *
     * @returns {string}
     */
    static get ezServicesVersion() {
        return this.#ezServicesVersion;
    }
    /**
     * @public @property @setter
     * Sets the ezclocker.services.version value from version.properties file
     *
     * @param {string} servicesVersion
     */
    static set ezServicesVersion(servicesVersion) {
        this.#ezServicesVersion = EzString.stringWithLengthOrDefault(servicesVersion, EzVersionChecker.DEFAULT_SERVICE_VERSION);
    }
    /**
     * @public @property @getter
     * Gets the ezclocker.services.version value from version.properties file
     *
     * @returns {string}
     *
     * @deprecated
     * Migrate to: EzVersionChcker.ezServicesVersion
     */
    static get servicesVersion() {
        return this.#ezServicesVersion;
    }

    /**
     * @private @field
     * Stores the ezclocker.build.version value from version.properties file
     *
     * @type {string}
     */
    static #ezVersion = EzString.EMPTY;
    /**
     * @public @property @getter
     * Gets the ezclocker.build.version value from version.properties file
     *
     * @returns {string}
     */
    static get ezVersion() {
        return this.#ezVersion;
    }
    /**
     * @public @property @setter
     * Sets the ezclocker.build.version value from version.properties file
     *
     * @param {string} version
     */
    static set ezVersion(version) {
        this.#ezVersion = EzString.stringOrEmpty(version);
    }
    /**
     * @public @property @getter
     * Gets the ezclocker.build.version value from version.properties file
     *
     * @returns {string}
     *
     * @deprecated
     * Migrate to: EzVersionChcker.ezVersion
     */
    static get version() {
        return this.ezVversion;
    }

    /**
     * @private @field
     * Stores the ezclocker.build.rc value from version.properties file
     *
     * @type {string}
     */
    static #ezBuild = EzString.EMPTY;
    /**
     * @public @property @getter
     * Gets the ezclocker.build.rc value from version.properties file
     *
     * @returns {string}
     */
    static get ezBuild() {
        return this.#ezBuild;
    }
    /**
     * @public @property @setter
     * Sets the ezclocker.build.rc value from version.properties file
     *
     * @param {string} build
     */
    static set ezBuild(build) {
        this.#ezBuild = EzString.stringOrEmpty(build);
    }
    /**
     * @public @property @getter
     * Gets the ezclocker.build.rc value from version.properties file
     *
     * @returns {string}
     *
     * @deprecated
     * Migrate to: EzVersionChecker.ezBuild
     */
    static get build() {
        return this.ezBuild;
    }

    /**
     * @private @field
     * Stores the ezclocker.target-env value from version.properties file
     *
     * @type {string}
     */
    static #ezEnviornment = EzString.EMPTY;
    /**
     * @public @property @getter
     * Gets the ezclocker.target-env value from version.properties file
     *
     * @returns {string}
     */
    static get ezEnviornment() {
        return this.#ezEnviornment;
    }
    /**
     * @public @property @setter
     * Sets the ezclocker.target-env value from version.properties file
     *
     * @param {string} ezEnviornment
     */
    static set ezEnviornment(ezEnviornment) {
        this.#ezEnviornment = EzString.stringOrDefault(ezEnviornment, EzVersionChecker.DEFAULT_BUILD_ENVIRONMENT);
    }
    /**
     * @public @property @getter
     * Gets the ezclocker.target-env value from version.properties file
     *
     * @returns {string}
     *
     * @deprecated
     * Migrate to: EzVersionChecker.ezEnviornment;
     */
    static get ezClockerEnv() {
        return this.ezEnvironment;
    }
    /**
     * @public @property @getter
     * Gets the ezclocker.build-environment value from version.properties file
     *
     * @returns {string}
     *
     * @deprecated
     * Migrate to: EzVersionChecker.ezEnviornment;
     */
    static get ezClockerEnvironment() {
        return this.ezEnvironment;
    }

    /**
     * @private @field
     * Stores the ezclocker.build.datetime value from version.properties file
     *
     * @type {string}
     */
    static #ezBuildDate = EzString.EMPTY;
    /**
     * @public @property @getter
     * Gets the ezclocker.build.datetime value from version.properties file
     *
     * @returns {string}
     */
    static get ezBuildDate() {
        return this.#ezBuildDate;
    }
    /**
     * @public @property @setter
     * Sets the ezclocker.build.datetime value from version.properties file
     *
     * @param {string} buildDate
     */
    static set ezBuildDate(buildDate) {
        this.#ezBuildDate = EzString.stringWithLengthOrDefault(buildDate, EzVersionChecker.DEFAULT_BUILD_DATE);
    }
    /**
     * @public @property @getter
     * Gets the ezclocker.build.datetime value from version.properties file
     *
     * @returns {string}
     *
     * @deprecated
     * Migreate to: EzVersionChcker.ezBuildDate
     */
    static get buildDate() {
        return this.ezBuildDate;
    }

    /**
     * @private @field
     * Stores the ezclocker.build.branch value from version.properties file
     * Branch is only available for ROLE_ADMIN or ROLE_EZCLOCKER_SUPPORT
     *
     * @type {string}
     */
    static #ezBranch = EzString.EMPTY;
    /**
     * @public @property @getter
     * Gets the ezclocker.build.branch value from version.properties file
     * Branch is only available for ROLE_ADMIN or ROLE_EZCLOCKER_SUPPORT
     *
     * @returns {string}
     */
    static get ezBranch() {
        return this.#ezBranch;
    }
    /**
     * @public @property @setter
     * Sets the ezclocker.build.branch value from version.properties file
     *
     * @param {string} branch
     */
    static set ezBranch(branch) {
        this.#ezBranch = EzString.stringWithLengthOrDefault(branch, 'UNKNOWN');
    }

    /**
     * @private @field
     * Stores the version info JSON object
     *
     * @type {object}
     */
    static #ezVersionInfo = null;
    /**
     * @public @property @getter
     * Gets the EzVersionInfo object
     *
     * @returns {object}
     */
    static get ezVersionInfo() {
        return EzVersionChecker.#ezVersionInfo;
    }
    /**
     * @public @property @setter
     * Sets
     *
     * @param {object|string} versionInfo
     * Default: null
     */
    static set ezVersionInfo(ezVersionInfo = null) {
        EzVersionChecker.#ezVersionInfo = EzObject.assignOrNull(ezVersionInfo);
    }

    /**
     * @public @Method
     * Gets the value of the provided url param
     *
     * @param {string} paramName
     *
     * @returns {string}
     */
    static ezReadBrowserUrlParamValue(paramName) {
        if (!EzString.hasLength(paramName)) {
            return EzString.EMPTY;
        }

        const regexPattern = new RegExp(`([?&]${paramName}=).*(\\?|&|#|)`);

        const url = window.location.href;

        const matches = url.match(regexPattern);

        if (!EzArray.hasLength(matches)) {
            return EzString.EMPTY;
        }

        const value = matches[0].split('=');

        return 2 === value.length ? EzVersionChecker.decodeURL(value[1]) : EzString.EMPTY;
    }

    /**
     * @static
     * @public @method
     * Decodes URL String
     *
     * @param {string} value
     * @param {boolean} replacePlusWithSpace
     */
    static decodeURL(value, replacePlusWithSpace = true) {
        if (!EzString.hasLength(value)) {
            return EzString.EMPTY;
        }

        return EzBoolean.isTrue(replacePlusWithSpace) ? decodeURIComponent(value).replaceAll('+', ' ') : decodeURIComponent(value);
    }

    /**
     * @public @method
     * Verifies the current page has the current version param. If not,
     * the page is reloaded with the correct version param.
     *
     * @param {boolean} booleanUpdateUrl
     * Default: false
     *
     * @returns {boolean}
     * Returns true if the page has the correct version param. False otherwise.
     */
    static ezVerifyPageVersion(booleanUpdateUrl = false) {
        const currentUrlValue = EzVersionChecker.ezReadBrowserUrlParamValue(EzVersionChecker.VERSION_URL_PARAM_NAME);

        if (EzString.hasLength(currentUrlValue) && currentUrlValue === EzVersionChecker.ezWebsiteVersion) {
            return true;
        }

        if (EzBoolean.isTrue(booleanUpdateUrl)) {
            EzVersionChecker.ezUpdatePageUrlVersion();
        }

        return false;
    }

    /**
     * @public @method
     * Hides the document's body HTML
     */
    static ezHideDocumentBody() {
        if (EzObject.isValid(globalThis?.document?.body)) {
            globalThis.document.body.style.display = 'none';
        }
    }

    /**
     * @public @method
     * Fades in and shows the documents body html
     */
    static ezShowDocumentBody() {
        if (EzObject.isValid(globalThis?.document?.body)) {
            globalThis.document.body.classList.add('animate__animated');

            globalThis.document.body.classList.add('animate__fadeIn');

            globalThis.document.body.style.display = 'unset';
        }
    }

    /**
     * @public @method
     * Updates the browser's url with the current version param.
     */
    static ezUpdatePageUrlVersion() {
        const updatedUrl = EzVersionChecker.ezReplaceUrlParam(window.location.href, EzVersionChecker.VERSION_URL_PARAM_NAME, EzVersionChecker.ezWebsiteVersion);

        // Update the url
        window.location.assign(updatedUrl);
    }

    /**
     * @public @method
     * Replaces the url param value with name paramName with the provided paramValue. If the param doesn't exist, it is appended to the url.
     *
     * @param {string} url
     * @param {string} paramName
     * @param {string|number} paramValue
     *
     * @returns {string}
     */
    static ezReplaceUrlParam(url, paramName, paramValue) {
        if (!EzString.hasLength(paramName)) {
            throw new EzBadParamException('paramName', EzVersionChecker, EzVersionChecker.ezReplaceUrlParam);
        }
        if (!EzString.hasLength(url)) {
            return `?${paramName}=${EzString.stringOrEmpty(paramValue)}`;
        }

        const pattern = new RegExp(`([?&]${paramName}=).*(\\?|&|#|)`);

        if (-1 < url.search(pattern)) {
            return url.replace(pattern, `$1${EzString.stringOrEmpty(paramValue)}$2`);
        }

        // Appending new param
        const paramDelimiter = -1 === url.indexOf('?') ? '?' : '&';

        return `${url}${paramDelimiter}${paramName}=${EzString.stringOrEmpty(paramValue)}`;
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Class Initialization and EzApi Registration Section
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

    /**
     * @public @method
     * Loads the EzVersionChecker from the version.properties file.
     *
     * Example version info:
     *  {
     *     "ezclocker.website.version": "r75-5",
     *     "ezclocker.services.version": "r75.2.0.2",
     *     "ezclocker.build.rc": "r75.2.0.2",
     *     "ezclocker.build.version": "2024.12.06-75.0.2",
     *     "ezclocker.build.branch": "releases/r75.2.0.x",
     *     "ezclocker.build.datetime": "2024-12-06T20:57:57-06:00",
     *     "ezclocker.build-environment": "loc"
     *  }
     *
     * @returns {Promise.resolve}
     */
    static ezLoadEzClockerVersionInfo() {
        return EzPromise.asyncAction(
            (finished) =>
                axios('/version.properties').then(
                    (versionInfoResponse) => {
                        EzVersionChecker.ezVersionInfo = EzString.hasLength(versionInfoResponse) ? EzJson.fromJson(versionInfoResponse) : null;

                        if (EzString.isString(EzVersionChecker.ezVersionInfo)) {
                            EzVersionChecker.ezVersionInfo = null;

                            EzConsole.error(
                                EzString.em`
                                    Unable to load the version.properties information.
                                    Error response: ${versionInfoResponse}`
                            );
                        }

                        EzVersionChecker.ezWebsiteVersion = EzString.stringOrDefault(
                            EzVersionChecker?.ezVersionInfo?.['ezclocker.website.version'],
                            EzVersionChecker.DEFAULT_WEBSITE_VERSION);

                        if (EzVersionChecker.DEFAULT_WEBSITE_VERSION !== EzVersionChecker.ezWebsiteVersion) {
                            EzVersionChecker.DEFAULT_WEBSITE_VERSION = EzVersionChecker.ezWebsiteVersion;
                        }

                        EzVersionChecker.ezServicesVersion = EzString.stringOrDefault(
                            EzVersionChecker?.ezVersionInfo?.['ezclocker.services.version'],
                            EzVersionChecker.DEFAULT_SERVICES_VERSION);

                        if (EzVersionChecker.DEFAULT_SERVICES_VERSION !== EzVersionChecker.ezServicesVersion) {
                            EzVersionChecker.DEFAULT_SERVICES_VERSION = EzVersionChecker.ezServicesVersion;
                        }

                        EzVersionChecker.ezBuild = EzString.stringOrDefault(
                            EzVersionChecker?.ezVersionInfo?.['ezclocker.build.version'],
                            EzVersionChecker.DEFAULT_BUILD);

                        if (EzVersionChecker.DEFAULT_BUILD !== EzVersionChecker.ezBuild) {
                            EzVersionChecker.DEFAULT_BUILD = EzVersionChecker.ezBuild;
                        }

                        EzVersionChecker.ezVersion = EzString.stringOrDefault(
                            EzVersionChecker?.ezVersionInfo?.['ezclocker.build.version'],
                            EzVersionChecker.DEFAULT_BUILD_VERSION);

                        if (EzVersionChecker.DEFAULT_BUILD_VERSION !== EzVersionChecker.ezVersion) {
                            EzVersionChecker.DEFAULT_BUILD_VERSION = EzVersionChecker.ezVersion;
                        }

                        EzVersionChecker.ezBranch = EzString.stringOrDefault(
                            EzVersionChecker?.ezVersionInfo?.['ezclocker.build.branch'],
                            EzVersionChecker.DEFAULT_BUILD_BRANCH);

                        if (EzVersionChecker.DEFAULT_BUILD_BRANCH !== EzVersionChecker.ezBranch) {
                            EzVersionChecker.DEFAULT_BUILD_BRANCH = EzVersionChecker.ezBranch;
                        }

                        EzVersionChecker.ezbuildDate = EzString.stringOrDefault(
                            EzVersionChecker?.ezVersionInfo?.['ezclocker.build.datetime'],
                            EzVersionChecker.DEFAULT_BUILD_DATE);

                        if (EzVersionChecker.DEFAULT_BUILD_DATE !== EzVersionChecker.ezbuildDate) {
                            EzVersionChecker.DEFAULT_BUILD_DATE = EzVersionChecker.ezbuildDate;
                        }


                        EzVersionChecker.ezEnvironment = EzString.stringOrDefault(
                            EzVersionChecker?.ezVersionInfo?.['ezclocker.build-environment'],
                            EzVersionChecker.DEFAULT_BUILD_ENVIRONMENT);

                        if (EzVersionChecker.DEFAULT_BUILD_ENVIRONMENT !== EzVersionChecker.ezEnvironment) {
                            EzVersionChecker.DEFAULT_BUILD_ENVIRONMENT = EzVersionChecker.ezEnvironment;
                        }

                        return finished();
                    },
                    (eResponse) => {
                        EzConsole.error(
                            EzString.em`
                            Unable to load the version.properties information.
                            Error response: ${eResponse}`
                        );

                        EzVersionChecker.ezWebsiteVersion = EzVersionChecker.DEFAULT_WEBSITE_VERSION;

                        EzVersionChecker.ezServicesVersion = EzVersionChecker.DEFAULT_SERVICES_VERSION;

                        EzVersionChecker.ezBuild = EzVersionChecker.DEFAULT_BUILD;

                        EzVersionChecker.ezVersion = EzVersionChecker.DEFAULT_BUILD_VERSION;

                        EzVersionChecker.ezBranch = EzVersionChecker.DEFAULT_BUILD_BRANCH;

                        EzVersionChecker.ezBuildDate = EzVersionChecker.DEFAULT_BUILD_DATE;

                        EzVersionChecker.ezEnvironment = EzVersionChecker.DEFAULT_BUILD_ENVIRONMENT;

                        return finished();
                    }
                )
        );
    }

    /**
     * Static Initialization of EzVersionChecker
     */
    static {
        // Disabled automatic version check due to query param defects
        //EzVersionChecker.ezLoadEzClockerVersionInfo().then(() => EzVersionChecker.ezVerifyPageVersion(true));
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond above section !!
    // NO CODE AFTER THIS SECTION
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
}
